import {
	IconCheckOutline18 as Check,
	IconCircleInfoOutline18 as CircleInfo,
	IconCircleWarningOutline18 as CircleWarning,
	IconLightbulbOutline18 as Lightbulb,
	IconOctagonWarningOutline18 as OctagonWarning,
	IconTriangleWarningOutline18 as TriangleWarning,
} from "nucleo-ui-outline-18"

import type { CalloutVariant } from "@productlane/lib/src/tiptap/extensions/callout-variant"
import { cn } from "@productlane/lib/src/cn"
import { readCalloutVariant } from "@productlane/lib/src/tiptap/extensions/callout-variant"
import { Tooltip } from "@productlane/ui"

import type { Editor } from "@tiptap/react"
import { EditorMenuButton } from "./menu/editor-menu-button"

const VARIANTS: {
	variant: CalloutVariant
	label: string
	Icon: typeof CircleInfo
	color: string
}[] = [
	{
		variant: "note",
		label: "Note",
		Icon: CircleWarning,
		color: "text-blue-600",
	},
	{
		variant: "neutral",
		label: "Info",
		Icon: CircleInfo,
		color: "text-gray-600",
	},
	{ variant: "tip", label: "Tip", Icon: Lightbulb, color: "text-green-600" },
	{ variant: "check", label: "Check", Icon: Check, color: "text-green-600" },
	{
		variant: "warning",
		label: "Warning",
		Icon: TriangleWarning,
		color: "text-yellow-600",
	},
	{
		variant: "danger",
		label: "Danger",
		Icon: OctagonWarning,
		color: "text-red-600",
	},
]

// Older callouts store `success`, which renders as Check.
const pickerVariant = (variant: CalloutVariant): CalloutVariant =>
	variant === "success" ? "check" : variant === "info" ? "note" : variant

/**
 * Colour picker for the callout the cursor sits in. Renders nothing outside
 * one, so the menu only grows where the choice exists.
 */
export function CalloutVariantMenu({ editor }: { editor: Editor }) {
	if (!editor.isActive("blockquote")) return null

	const active = pickerVariant(
		readCalloutVariant(editor.getAttributes("blockquote").calloutVariant),
	)

	return (
		<>
			{VARIANTS.map(({ variant, label, Icon, color }) => (
				<Tooltip key={variant} shouldWrapChildren label={`${label} callout`}>
					<EditorMenuButton
						onClick={() =>
							editor
								.chain()
								.focus(undefined, { scrollIntoView: false })
								.setCalloutVariant(variant)
								.run()
						}
						isActive={active === variant}
					>
						<Icon className={cn("h-4 w-4 flex-none", color)} />
					</EditorMenuButton>
				</Tooltip>
			))}
		</>
	)
}
